import {ChangeDetectionStrategy, Component, OnInit, ViewChild} from '@angular/core';
import {formatDate} from '@angular/common';
import {combineLatest, map} from 'rxjs';
import {Dropdown} from 'primeng/dropdown';
import {TranslateService} from '@ngx-translate/core';
import {TeamDynamicFacade} from '../../facade/team-dynamic.facade';
import {TeamDynamicRequestDto} from '../../dto/team-dynamic-request.dto';

@Component({
  selector: 'app-team-dynamic-main-page',
  templateUrl: './team-dynamic-main-page.component.html',
  styleUrls: ['./team-dynamic-main-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TeamDynamicMainPageComponent implements OnInit {
  @ViewChild('periodDropdown') periodDropdown: Dropdown | undefined;

  readonly vm$ = combineLatest([
    this.teamDynamicFacade.loading$,
    this.teamDynamicFacade.overallScore$,
    this.teamDynamicFacade.pyramidCategories$,
    this.teamDynamicFacade.pyramidLegend$,
    this.teamDynamicFacade.scoreDevelopment$,
    this.teamDynamicFacade.loadingScoreDevelopment$,
    this.teamDynamicFacade.scoreDevelopmentChips$,
    this.teamDynamicFacade.teamOverview$,
    this.teamDynamicFacade.loadingTeamOverview$
  ]).pipe(
    map(([loading, overallScore, pyramidCategories, pyramidLegend, scoreDevelopment, loadingScoreDevelopment,
           scoreDevelopmentChips, teamOverview, loadingTeamOverview]) => ({
      loading,
      overallScore,
      pyramidCategories,
      pyramidLegend,
      scoreDevelopment,
      loadingScoreDevelopment,
      scoreDevelopmentChips,
      teamOverview,
      loadingTeamOverview
    }))
  );

  public view: 'overall' | 'indicator' = 'overall';
  public period: 'all' | 'custom' = 'all';
  public periodLabel: string = '';
  public periodOptions: { label: string, value: string }[] = [];
  public periodRangeDates: Date[] = [];
  public showPeriodCalendar: boolean = false;
  public activeChipUuid: string | null = null;

  private requestPayload: TeamDynamicRequestDto = {
    startDate: undefined,
    endDate: undefined,
    timedQuestionCollectionUuid: null
  };

  constructor(private teamDynamicFacade: TeamDynamicFacade, private translate: TranslateService) {
  }

  ngOnInit(): void {
    this.setPeriodOptions();
    this.setPeriodLabel();
    this.teamDynamicFacade.loadData(this.requestPayload);
  }

  setView(view: 'overall' | 'indicator') {
    this.view = view;
  }

  onPeriodChange(event: any) {
    if (event.value === 'all') {
      this.selectAllTime();
    }
  }

  selectAllTime() {
    this.period = 'all';
    this.periodRangeDates = [];
    this.showPeriodCalendar = false;
    this.setPeriodLabel();
    this.requestPayload = {...this.requestPayload, startDate: undefined, endDate: undefined};
    this.teamDynamicFacade.reloadForPeriod(this.requestPayload);
  }

  submitPeriodRange() {
    if (this.periodRangeDates.length !== 2 || !this.periodRangeDates[1]) {
      return;
    }
    this.period = 'custom';
    this.setPeriodLabel();
    this.periodDropdown?.hide();
    this.requestPayload = {
      ...this.requestPayload,
      startDate: formatDate(this.periodRangeDates[0], 'dd.MM.yyyy', 'en-US'),
      endDate: formatDate(this.periodRangeDates[1], 'dd.MM.yyyy', 'en-US')
    };
    this.teamDynamicFacade.reloadForPeriod(this.requestPayload);
  }

  clearPeriodRange() {
    this.periodRangeDates = [];
  }

  cancelPeriodRange() {
    this.clearPeriodRange();
    this.periodDropdown?.hide();
  }

  onScoreDevelopmentChip(uuid: string | null) {
    this.activeChipUuid = uuid;
    this.requestPayload = {...this.requestPayload, timedQuestionCollectionUuid: uuid};
    this.teamDynamicFacade.loadScoreDevelopment(this.requestPayload);
  }

  private setPeriodOptions() {
    this.periodOptions = [
      {label: this.translate.instant('custom_poll_main_reporting_all_time'), value: 'all'},
      {label: this.translate.instant('wo_team_dynamic_custom_period'), value: 'custom'}
    ];
  }

  private setPeriodLabel() {
    this.periodLabel = this.period === 'custom' && this.periodRangeDates.length === 2 ?
      formatDate(this.periodRangeDates[0], 'dd.MM.yyyy', 'en-US') + ' - ' +
      formatDate(this.periodRangeDates[1], 'dd.MM.yyyy', 'en-US') :
      this.translate.instant('custom_poll_main_reporting_all_time');
  }
}
